import { XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';

import type { RBACGroup } from '../../../types';

type Props = {
  displayName: string;
  roleId: string;
  selected?: boolean;
  transitive?: boolean;
  deleted?: boolean;
  added?: boolean;
  // When false, the row is informational only: no selection/keyboard handling and
  // not announced as a listbox option. Used for directly-held ACLs, which have no
  // parent role to highlight, so wiring them into role selection would be a no-op.
  selectable?: boolean;
  // The group is mastered in on-prem AD — its membership can't be changed from the
  // cloud, so badge it and suppress the delete control (like transitive). See
  // isOnPremGroup.
  onPrem?: boolean;
  // Provenance caption: the role an ACL came from, or the group(s) a transitive
  // role was inherited through.
  derivedFrom?: string;
  onClick?: (groupId: string) => void;
  onDelete?: (group: RBACGroup) => void;
};

export default function GroupDisplayItem({
  displayName: name,
  selected = false,
  transitive = false,
  roleId,
  deleted = false,
  added = false,
  selectable = true,
  onPrem = false,
  derivedFrom,
  onClick,
  onDelete,
}: Props) {
  const handleDeleteClick = (e: React.MouseEvent) => {
    // Don't let the click bubble up to the parent element
    e.stopPropagation();
    // onDelete is optional, so we need to check if it's defined
    if (onDelete) onDelete({ displayName: name, id: roleId });
  };

  const className = classNames(
    // Muted but legible on both themes — base-300 as a text colour is near the
    // background and was effectively invisible on the dark theme. Drop the muting
    // when the row is selected so the vivid bg-info highlight stays legible.
    { 'text-base-content/60': transitive && !selected },
    { 'text-info-content bg-info': selected },
    { 'text-error-content bg-error': deleted },
    { 'text-success-content bg-success': added },
    'p-2',
    'flex',
    'justify-between',
    'items-center',
    { 'cursor-pointer': selectable }
  );

  const displayNameClassName = classNames({ underline: selected }, 'truncate');

  // Only the selectable variant is an interactive listbox option; the informational
  // variant (directly-held ACLs) carries no role/handlers. Grouping the interactive
  // attributes keeps the element statically consistent for a11y (role present
  // whenever click/key handlers are).
  const interactiveProps = selectable
    ? {
        'aria-selected': selected,
        onClick: () => onClick?.(roleId),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(roleId);
          }
        },
        role: 'option',
        tabIndex: 0,
      }
    : {};

  return (
    <li className={className} {...interactiveProps}>
      <div className="min-w-0">
        <div className={displayNameClassName}>{name}</div>
        {derivedFrom && (
          // opacity (not a fixed colour) so it inherits the row's text colour —
          // muted normally, but dark like the rest when the row is highlighted.
          <div className="text-xs opacity-70 truncate" title={`Derived from ${derivedFrom}`}>
            from {derivedFrom}
          </div>
        )}
      </div>

      {transitive && (
        <span
          className="mr-1 ml-2 badge badge-accent"
          title="Inherited via a nested group membership — it can't be removed here; manage it in the source group."
        >
          Transitive
        </span>
      )}

      {!transitive && onPrem && (
        <span
          className="mr-1 ml-2 badge badge-info"
          title="Mastered in on-prem AD — its membership can't be changed from RBACMan; manage it in Active Directory."
        >
          On-prem AD
        </span>
      )}

      {!transitive && !onPrem && onDelete ? ( // No delete for transitive or on-prem-mastered groups, or when onDelete is undefined
        <div>
          <button className="btn btn-ghost btn-xs btn-circle" onClick={handleDeleteClick}>
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </li>
  );
}
